import React, { useEffect } from 'react';
import { ApolloProvider } from '@apollo/react-common';
import { useDispatch, useSelector } from 'react-redux';
import { Router, Switch } from 'react-router-dom';
import { setupDatePicker, ToastContainer } from '@modernhealth/vital-components';
import { AnalyticsProvider, Route } from '@modernhealth/web-analytics';
import { useTranslation } from 'react-i18next';
import { IntlProvider } from 'react-intl';
import { MessagingProvider } from '@modernhealth/messaging-kit';

import 'react-responsive-modal/lib/react-responsive-modal.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { analyticsClient } from 'src/shared/services/analytics';
import { getUserTimeZone } from 'src/modules/user/selectors/userSelectors';

import { AppContext } from './modules/auth/containers/AppContext';
import { appClient } from './config/apollo';
import { history } from './config/history';
import { Dispatch } from './config/rootStore';
import { ApolloToReduxSyncManager } from './config/ApolloToReduxSyncManager';
import { MobileAppInformationModalDashboard } from './modules/dashboard/components/MobileAppInformationModalDashboard';
import { Routes } from './routing/Routes';
import { PageNotFound } from './shared/components/PageNotFound';
import { messagingClient } from './modules/messaging/messagingClient';
import { RouteProvider } from './routing/RouteContext';
import { SearchParams } from './routing/SearchParams';
import { DSTranslationProvider } from './shared/ds/providers/translationProvider';
import { LanguagePreferenceListener } from './modules/user/components/LanguagePreferenceListener';
import { FeatureFlagsManager } from './config/FeatureFlagsManager';
import { captureException } from './shared/helpers/captureException';

// Wrap this in a try-catch in case the browser doesn't support
// custom elements. (only quite old browsers)
try {
    setupDatePicker();
} catch (error) {
    captureException(error);
}

export const Root = () => {
    const { i18n } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const timeZone = useSelector(getUserTimeZone);

    useEffect(() => {
        dispatch.countries.fetchCountries();
    }, [dispatch]);

    return (
        <IntlProvider timeZone={timeZone || undefined} locale={i18n.language}>
            <Router history={history}>
                <SearchParams />
                <RouteProvider>
                    <ApolloProvider client={appClient}>
                        <ApolloToReduxSyncManager client={appClient} />
                        <AnalyticsProvider analyticsClient={analyticsClient}>
                            <MessagingProvider messagingClient={messagingClient}>
                                <AppContext>
                                    <LanguagePreferenceListener />
                                    <DSTranslationProvider>
                                        <FeatureFlagsManager />
                                        <Switch>
                                            <Routes />
                                            <Route
                                                component={PageNotFound}
                                                analytics={{ pageName: 'not-found' }}
                                            />
                                        </Switch>
                                    </DSTranslationProvider>
                                </AppContext>
                                <MobileAppInformationModalDashboard />
                                <ToastContainer />
                            </MessagingProvider>
                        </AnalyticsProvider>
                    </ApolloProvider>
                </RouteProvider>
            </Router>
        </IntlProvider>
    );
};
